import { compact, concat, countBy, filter, flatMap, includes, map, size, uniq, uniqBy } from 'lodash';

import { FileInfo, UploadCaseSummary, UploadListResponse } from 'interfaces/fileList';
import { Job, JobStatus, JobType } from 'interfaces/job';

// The browser clock and the server clock can disagree; look slightly before the modal was opened.
export const NEW_UPLOAD_CLOCK_SKEW_TOLERANCE_MS = 2 * 60 * 1000;

export const getNewUploadsCutoffMs = (openedAt: number) => openedAt - NEW_UPLOAD_CLOCK_SKEW_TOLERANCE_MS;

const HAS_TIME_ZONE = /(Z|[+-]\d{2}:?\d{2})$/i;

// The backend stores UTC; a timestamp without a zone designator is UTC, not browser-local time.
const parseServerTimestamp = (timestamp: string | null | undefined) => {
  if (!timestamp) {
    return Number.NaN;
  }

  return Date.parse(HAS_TIME_ZONE.test(timestamp) ? timestamp : `${timestamp}Z`);
};

const isOnOrAfter = (timestamp: string | null | undefined, cutoffMs: number) => {
  const parsed = parseServerTimestamp(timestamp);
  return !Number.isNaN(parsed) && parsed >= cutoffMs;
};

const getUploadRows = (uploadList?: UploadListResponse): FileInfo[] =>
  uniqBy(
    concat(uploadList?.all || [], uploadList?.slides?.pending || [], uploadList?.slides?.processed || []),
    (row) => row.objectKey || row.id
  );

export const getNewUploadRows = (uploadList: UploadListResponse | undefined, cutoffMs: number) =>
  filter(getUploadRows(uploadList), (row) => !row.isHidden && isOnOrAfter(row.date, cutoffMs));

export interface NewIngestedSlide {
  key: string;
  slideId: string;
  name: string;
  cases: UploadCaseSummary[];
}

const getRowName = (row: FileInfo) => row.fileName || row.name;

/** One entry per slide ingested after the cutoff. A re-ingested upload lists every ingest, never one merged slide. */
export const getNewIngestedSlides = (
  uploadList: UploadListResponse | undefined,
  cutoffMs: number
): NewIngestedSlide[] =>
  flatMap(getUploadRows(uploadList), (row): NewIngestedSlide[] => {
    if (row.isHidden) {
      return [];
    }

    if (size(row.duplicateIngests)) {
      return compact(
        map(row.duplicateIngests, (ingest) =>
          ingest.slideId && !ingest.isArchived && isOnOrAfter(ingest.slideCreatedAt, cutoffMs)
            ? {
                key: `${row.objectKey || row.id}:${ingest.slideId}`,
                slideId: ingest.slideId,
                name: getRowName(row),
                cases: ingest.cases || [],
              }
            : null
        )
      );
    }

    const hasUsableIngest = !row.latestSuccessfulIngestJobArchived && Boolean(row.slideId);
    if (!hasUsableIngest || !isOnOrAfter(row.latestSuccessfulIngestJob?.startedAt, cutoffMs)) {
      return [];
    }

    return [
      {
        key: `${row.objectKey || row.id}:${row.slideId}`,
        slideId: row.slideId as string,
        name: getRowName(row),
        cases: row.cases || [],
      },
    ];
  });

export const getNewJobs = <T extends Job>(jobs: T[] | undefined, cutoffMs: number) =>
  filter(jobs, (job) => isOnOrAfter(job.startedAt, cutoffMs));

export interface IngestJobStatusCounts {
  inProgress: number;
  completed: number;
  failed: number;
  other: number;
}

export const countIngestJobStatuses = (jobs: Job[]): IngestJobStatusCounts => {
  const counts = countBy(jobs, (job) => {
    if (job.status === JobStatus.Pending || job.status === JobStatus.Running) return 'inProgress';
    if (job.status === JobStatus.Completed) return 'completed';
    if (includes([JobStatus.Failed, JobStatus.FailedToStart, JobStatus.Cancelled], job.status)) return 'failed';
    return 'other';
  });

  return {
    inProgress: counts.inProgress || 0,
    completed: counts.completed || 0,
    failed: counts.failed || 0,
    other: counts.other || 0,
  };
};

export type SlideTileState = 'ready' | 'generating' | 'failed' | 'notStarted' | 'unknown';

const getManifestSlideIds = (job: Job): string[] =>
  'manifest' in job && Array.isArray(job.manifest)
    ? compact(
        map(job.manifest as unknown[], (entry) =>
          typeof entry === 'string' ? entry : (entry as { slide_id?: string } | null)?.slide_id
        )
      )
    : [];

// Post-ingest tile jobs carry their slide in params.slide_id and an empty manifest.
const getTileJobSlideIds = (job: Job): string[] => {
  const paramsSlideId = (job as { params?: { slide_id?: unknown } }).params?.slide_id;

  return uniq(
    typeof paramsSlideId === 'string' && paramsSlideId
      ? [...getManifestSlideIds(job), paramsSlideId]
      : getManifestSlideIds(job)
  );
};

/**
 * A batch parent job reports its children, so per-slide child jobs decide each slide's tile state;
 * a parent without children is used as is.
 */
const flattenTileJobs = (jobs: Job[]): Job[] =>
  flatMap(jobs, (job) => (job.isParentJob && size(job.children) ? job.children || [] : [job]));

/**
 * `slideIdsWithIncompleteEvidence` lists slides whose tile jobs may not all have been loaded. Without a
 * completed or running job, their state is unknown rather than failed or not started.
 */
export const getTileStateBySlideId = (
  tileJobs: Job[] | undefined,
  slideIds: string[],
  slideIdsWithIncompleteEvidence: ReadonlySet<string> = new Set()
): Record<string, SlideTileState> => {
  const jobs = flattenTileJobs(filter(tileJobs, (job) => job.type === JobType.TileGeneration));
  const statusesBySlideId: Record<string, (string | undefined)[]> = {};
  jobs.forEach((job) => {
    getTileJobSlideIds(job).forEach((slideId) => {
      statusesBySlideId[slideId] = [...(statusesBySlideId[slideId] || []), job.status];
    });
  });

  const result: Record<string, SlideTileState> = {};
  slideIds.forEach((slideId) => {
    const statuses = statusesBySlideId[slideId] || [];
    if (includes(statuses, JobStatus.Completed)) {
      result[slideId] = 'ready';
    } else if (includes(statuses, JobStatus.Pending) || includes(statuses, JobStatus.Running)) {
      result[slideId] = 'generating';
    } else if (slideIdsWithIncompleteEvidence.has(slideId)) {
      result[slideId] = 'unknown';
    } else if (size(statuses)) {
      result[slideId] = 'failed';
    } else {
      result[slideId] = 'notStarted';
    }
  });

  return result;
};
